import { ReactElement, useCallback, useEffect } from "react";
import { Box, Button, Flex } from "@chakra-ui/react";
import { BasicPageLayout } from "../templates/BasicPageLayout";

import { PasswordInput } from "../atoms/AppInputs";
import { SubmitHandler, useForm } from "react-hook-form";
import { useTranslation } from "react-i18next";
import { useAtom } from "jotai";
import { atomWithReset, useResetAtom } from "jotai/utils";
import { employeeInfoAtom, passwordChangeStatusAtom } from "../../store";

import { useNavigate } from "react-router-dom";
import { voislogApi } from "../../utils/apiClients/voislogApi";
import { ApiError } from "../../utils/apiClients/ApiError";
import { useToastMessage } from "../../hooks/useToastMessage";

import { ChangeSelectionUserPassword } from "../../utils/db";
import { useModifiedTranslation } from "../../hooks/useModifiedTranslation";

const passwordErrorAtom = atomWithReset(false);
const showDialogAtom = atomWithReset(false);
const contentAtom = atomWithReset("");
const sessionErrorAtom = atomWithReset(false);

type passwordInfo = (password: string, employeeId: string) => void;

function useSetPasswordChange(): passwordInfo {
  const resetContent = useResetAtom(contentAtom);
  const resetSessionError = useResetAtom(sessionErrorAtom);
  const resetPasswordError = useResetAtom(passwordErrorAtom);
  const [showDialog] = useAtom(showDialogAtom);
  const navigate = useNavigate();
  const [, setPasswordChangeStatus] = useAtom(passwordChangeStatusAtom);
  const toastMessage = useToastMessage();
  const { i18n } = useTranslation();

  return useCallback(
    async (password, employeeId) => {
      try {
        const { error, response } = await voislogApi.PUT(
          "/v2/core/auth/users/{user_id_or_me}/password",
          {
            params: { path: { user_id_or_me: "me" } },
            body: { new_password: password },
          },
        );
        if (error) throw new ApiError(response, "put", error);
        setPasswordChangeStatus(true);
        resetPasswordError();
        resetSessionError();
        resetContent();
        if (!showDialog) {
          ChangeSelectionUserPassword(employeeId, password);
          navigate(`/${i18n.resolvedLanguage}/` + "setting");
        }
      } catch (e) {
        console.error("Login Error!", e);
        toastMessage(e);
      }
    },
    [
      navigate,
      resetContent,
      resetPasswordError,
      resetSessionError,
      setPasswordChangeStatus,
      showDialog,
      toastMessage,
      i18n,
    ],
  );
}

type PasswordFormData = {
  password: string;
  conformPassword: string;
};

export function PasswordChange(): ReactElement {
  const [, setPasswordError] = useAtom(passwordErrorAtom);
  const [content, setContent] = useAtom(contentAtom);
  const [employeeInfo] = useAtom(employeeInfoAtom);
  const setPasswordChange = useSetPasswordChange();

  useEffect(() => {
    setContent("");
  }, [setContent]);

  const {
    register,
    handleSubmit,
    formState: { isSubmitting },
  } = useForm<PasswordFormData>();
  const t = useModifiedTranslation();

  const onSubmit: SubmitHandler<PasswordFormData> = async (data) => {
    if (data.password.length < 4 || data.conformPassword.length < 4) {
      // バリデーション（パスワードが3文字以下）
      setPasswordError(true);
      const error01 = t("PasswordReset.NewPassword.error01");
      setContent(error01);
    } else if (data.password === data.conformPassword) {
      // バリデーション通過、パスワード変更の実施
      setPasswordChange(data.password, employeeInfo.employee_id);
    } else {
      // バリデーション（パスワードが再入力値と不一致）
      setPasswordError(true);
      const error02 = t("PasswordReset.NewPassword.error02");
      setContent(error02);
    }
  };

  return (
    <BasicPageLayout
      title={t("PasswordReset.NewPassword.title")}
      showBackButton
      titleProps={{ fontSize: "2xl" }}
    >
      <Box
        sx={{
          ".input_field01": {
            paddingTop: "0px",
            paddingBottom: "0px",
          },
        }}
        as="form"
        onSubmit={handleSubmit(onSubmit)}
      >
        {content !== "" && (
          <Box color="primary.theme_lv1" marginLeft={10} marginBottom={2}>
            {content}
          </Box>
        )}
        <Flex className="input_field01" p={{ base: 5, md: 10 }}>
          <PasswordInput
            input={{
              ...register("password", { required: true }),
              placeholder: t("PasswordReset.NewPassword.input"),
            }}
          />
        </Flex>
        <Flex p={{ base: 5, md: 10 }}>
          <PasswordInput
            input={{
              ...register("conformPassword", { required: true }),
              placeholder: t("PasswordReset.NewPassword.confirm"),
            }}
          />
        </Flex>
        <Button
          type="submit"
          variant="btn_primary"
          w="full"
          size="lg"
          mb={10}
          isLoading={isSubmitting}
        >
          {t("PasswordChange.change")}
        </Button>
      </Box>
    </BasicPageLayout>
  );
}
